/**
 * The only place in the app that calls fetch.
 *
 * Everything about talking to the server lives here: the access token, the
 * silent refresh when it expires, error normalisation, and query building.
 * A component that needs data calls `api.get(...)`; it never sees a Response,
 * never checks `res.ok`, and never knows a token exists.
 *
 * The refresh is deliberately single-flight. Ten components mounting at once
 * on a stale token would otherwise fire ten refreshes, and because refresh
 * tokens rotate, nine of them would be replays — which the server correctly
 * treats as a stolen token and responds to by ending every session.
 */

const BASE = import.meta.env.VITE_API_BASE || '';

let accessToken = null;
let onSessionLost = null;
let refreshInFlight = null;

export class ApiError extends Error {
  constructor(status, code, message, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** True when retrying could plausibly work. */
  get isTransient() {
    return this.status >= 500 || this.status === 0;
  }
}

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** Called when the session cannot be recovered, so the app can show the login screen. */
export function setSessionLostHandler(handler) {
  onSessionLost = handler;
}

function buildUrl(path, params) {
  const url = `${BASE}${path}`;
  if (!params) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, value instanceof Date ? value.toISOString() : String(value));
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

async function parse(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function refreshSession() {
  // Single-flight: concurrent callers await the same promise.
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) throw new ApiError(res.status, 'refresh_failed', 'Session expired');
      const body = await res.json();
      accessToken = body.accessToken;
      return body;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function request(method, path, { params, body, signal, retryOn401 = true } = {}) {
  let res;
  try {
    res = await fetch(buildUrl(path, params), {
      method,
      signal,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }

  // An expired access token is normal, not an error. Refresh once and retry.
  if (res.status === 401 && retryOn401 && path !== '/api/auth/refresh') {
    try {
      await refreshSession();
      return request(method, path, { params, body, signal, retryOn401: false });
    } catch {
      accessToken = null;
      if (onSessionLost) onSessionLost();
      throw new ApiError(401, 'session_expired', 'Your session has expired. Sign in again.');
    }
  }

  const payload = await parse(res);

  if (!res.ok) {
    const error = payload && payload.error ? payload.error : {};
    throw new ApiError(
      res.status,
      error.code || 'error',
      error.message || `Request failed (${res.status})`,
      payload
    );
  }

  return payload;
}

export const api = {
  get: (path, params, options) => request('GET', path, { params, ...options }),
  post: (path, body, options) => request('POST', path, { body, ...options }),
  put: (path, body, options) => request('PUT', path, { body, ...options }),
  patch: (path, body, options) => request('PATCH', path, { body, ...options }),
  delete: (path, options) => request('DELETE', path, options),

  /** Login is the one call that runs without a token. */
  async login(email, password) {
    const res = await fetch(`${BASE}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const payload = await parse(res);
    if (!res.ok) {
      const error = (payload && payload.error) || {};
      throw new ApiError(res.status, error.code || 'login_failed', error.message || 'Sign-in failed');
    }
    accessToken = payload.accessToken;
    return payload;
  },

  async logout() {
    try {
      await request('POST', '/api/auth/logout', { retryOn401: false });
    } finally {
      accessToken = null;
    }
  },

  refreshSession,
};
